import type { Placement } from '@popperjs/core';
import { memo, useEffect, type CSSProperties, type MouseEvent, type PropsWithChildren, type ReactNode } from 'react';
import styled from 'styled-components';

import { useIsMobile } from '../../hooks/useIsMobile';
import { Box } from '../Core';
import { Popover } from '../Popover/Popover';
import type { PopoverProps, PopoverStateProps } from '../Popover/types';
import { usePopoverState } from '../Popover/usePopoverState';

export interface TooltipProps extends PropsWithChildren<PopoverStateProps>, Pick<PopoverProps, 'zIndex'> {
  tooltip: ReactNode;
  placement?: Placement;
  delay?: number;
  preventOverflow?: boolean;
  /** If false, the tooltip content will not intercept pointer interactions with elements behind it. */
  interactive?: boolean;
  showUnderline?: boolean;
  disabled?: boolean;
  targetStyle?: CSSProperties;
  /** Set a custom maxWidth on the content of the tooltip. Defaults to 300px. */
  contentMaxWidth?: React.CSSProperties['maxWidth'];
  tooltipTestID?: string;
  contentStyle?: CSSProperties;
}

function hasTooltipValue(tooltip: ReactNode) {
  return tooltip !== null && tooltip !== undefined && tooltip !== false && tooltip !== '';
}

export const Tooltip = memo(function Tooltip(props: TooltipProps) {
  if (!hasTooltipValue(props.tooltip)) {
    return <TooltipTarget showUnderline={props.showUnderline}>{props.children}</TooltipTarget>;
  }

  return <TooltipWithPopover {...props} />;
});

function TooltipWithPopover({
  children,
  tooltip,
  trigger = 'hover',
  placement = 'top',
  delay = 200,
  hideDelay,
  showUnderline = false,
  disabled = false,
  isSmall = true,
  usePortal = true,
  contentMaxWidth = '300px',
  tooltipTestID,
  closeOnClickOutside,
  interactive = true,
  modifiers,
  resumeHoverOnTargetMount,
  ...props
}: TooltipProps) {
  const isMobile = useIsMobile();
  const popover = usePopoverState({
    trigger,
    placement,
    delay,
    hideDelay,
    modifiers,
    onOpen: props.onOpen,
    onClose: props.onClose,
    isSmall,
    usePortal,
    resumeHoverOnTargetMount,
    // Mobile opens the tooltip via tap, not hover, so an outside tap is the only way to dismiss it.
    closeOnClickOutside: closeOnClickOutside ?? isMobile,
  });

  const hasTooltipContent = !disabled;
  const { close, isOpen } = popover;

  useEffect(() => {
    if (disabled && isOpen) {
      close();
    }
  }, [close, disabled, isOpen]);

  // Stops the tap from also triggering an ancestor's tap handler (e.g. a card's own onPress).
  const onUnderlineClick =
    isMobile && hasTooltipContent
      ? (e: MouseEvent<HTMLSpanElement>) => {
          e.stopPropagation();
          popover.toggle();
        }
      : undefined;

  return (
    <Popover {...popover} {...props} pointerEvents={interactive ? undefined : 'none'} tabIndex={-1}>
      <>
        <TooltipTarget showUnderline={showUnderline} isOpen={popover.isOpen} onClick={onUnderlineClick}>
          {children}
        </TooltipTarget>
      </>
      {hasTooltipContent && (
        <Box data-testid={tooltipTestID} maxWidth={contentMaxWidth}>
          {tooltip}
        </Box>
      )}
    </Popover>
  );
}

function TooltipTarget({
  children,
  showUnderline = false,
  isOpen = false,
  onClick,
}: PropsWithChildren<
  Pick<TooltipProps, 'showUnderline'> & { isOpen?: boolean; onClick?: (e: MouseEvent<HTMLSpanElement>) => void }
>) {
  return showUnderline ? (
    <Underlined isOpen={isOpen} onClick={onClick}>
      {children}
    </Underlined>
  ) : (
    children
  );
}

export const Underlined = styled.span<{ isOpen: boolean }>`
  position: relative;
  display: flex;

  // Use pseudo-element here to prevent the dashed border from taking up space
  &::before {
    content: '';
    position: absolute;
    bottom: 0;
    width: 100%;
    border-bottom: 1px dotted;
    border-bottom-color: ${({ theme }) => theme.colorTextMuted};
    transition: border-bottom-color 200ms;
    ${({ isOpen, theme }) => isOpen && `border-bottom-color: ${theme.colorTextSubtle}`};
  }
`;
